import React, { useState, ReactNode } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Badge from '@amzn/awsui-components-console/badge';
import Input from '@amzn/awsui-components-console/input';
import Icon from '@amzn/awsui-components-console/icon';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';

interface EntityAttributesTableProps {
	attributes: Record<string, unknown>;
	title?: string;
}

const EntityAttributesTable: React.FC<EntityAttributesTableProps> = ({
	attributes,
	title = 'Additional Entity Attributes',
}) => {
	const [searchTerm, setSearchTerm] = useState('');

	const formatFieldName = (fieldName: string): string => {
		return fieldName
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const formatBoolean = (value: unknown): ReactNode => {
		if (value === 't' || value === 'true' || value === true) {
			return <Badge color="green">Yes</Badge>;
		}

		if (value === 'f' || value === 'false' || value === false) {
			return <Badge color="red">No</Badge>;
		}

		return String(value);
	};

	const formatList = (value: unknown): ReactNode => {
		if (
			typeof value === 'string' &&
			value.startsWith('[') &&
			value.endsWith(']')
		) {
			const content = value.slice(1, -1);
			const items = content.split(',').map((item) => item.trim());

			return (
				<SpaceBetween direction="horizontal" size="xxs">
					{items.map((item, idx) => (
						<Badge key={idx} color="blue">
							{item}
						</Badge>
					))}
				</SpaceBetween>
			);
		}

		return null;
	};

	const formatArray = (value: unknown): ReactNode => {
		if (Array.isArray(value)) {
			return (
				<SpaceBetween direction="horizontal" size="xxs">
					{value.map((item, idx) => (
						<Badge key={idx} color="blue">
							{item}
						</Badge>
					))}
				</SpaceBetween>
			);
		}

		return String(value);
	};

	const formatStatus = (key: string, value: unknown): ReactNode => {
		const statusFields = ['node_status', 'status'];

		if (statusFields.some((field) => key.toLowerCase().includes(field))) {
			const statusColors: Record<string, 'green' | 'blue' | 'red' | 'grey'> = {
				completed: 'green',
				verified: 'blue',
				failed: 'red',
				pending: 'grey',
                
			};
			const color = statusColors[String(value || '').toLowerCase()] || 'grey';

			return <Badge color={color}>{String(value)}</Badge>;
		}

		return null;
	};

	const formatValue = (key: string, value: unknown): ReactNode => {
		if (value === null || value === undefined || value === '') {
			return <Box color="text-status-inactive"><em>—</em></Box>;
		}

		if (value === 't' || value === 'f' || typeof value === 'boolean') {
			return formatBoolean(value);
		}

		const statusFormatted = formatStatus(key, value);

		if (statusFormatted !== null) {
			return statusFormatted;
		}

		if (Array.isArray(value)) {
			return formatArray(value);
		}

		const listFormatted = formatList(value);

		if (listFormatted !== null) {
			return listFormatted;
		}

		return String(value);
	};

	const filterAttributes = (attrs: Record<string, unknown>): Record<string, unknown> => {
		if (!searchTerm || searchTerm.trim() === '') return attrs;
		const filtered: Record<string, unknown> = {};
		const searchLower = searchTerm.toLowerCase().trim();

		Object.entries(attrs).forEach(([key, value]) => {
			const formattedKey = formatFieldName(key).toLowerCase();
			const valueStr = String(value || '').toLowerCase();

			if (
				key.toLowerCase().includes(searchLower) ||
				formattedKey.includes(searchLower) ||
				valueStr.includes(searchLower)
			) {
				filtered[key] = value;
			}
		});

		return filtered;
	};

	const filteredAttributes = filterAttributes(attributes);
	const attributeEntries = Object.entries(filteredAttributes);

	return (
		<ExpandableSection
			headerText={<strong>{title}</strong>}
			variant="container"
			defaultExpanded={true}
			headerActions={
				<Input
					type="search"
					placeholder="Search attributes..."
					value={searchTerm}
					onChange={({ detail }) => setSearchTerm(detail.value)}
					clearAriaLabel="Clear search"
				/>
			}
		>
			{attributeEntries.length === 0 ? (
				<Box textAlign="center" color="text-body-secondary" padding="xxl">
					<Icon name="search" size="big" />
					<Box variant="p" padding={{ top: 's' }}>
						No attributes found matching &quot;{searchTerm}&quot;
					</Box>
				</Box>
			) : (
				<ColumnLayout columns={2}>
					{attributeEntries.map(([key, value]) => (
						<Box key={key} padding={{ vertical: 's' }}>
							<Box variant="awsui-key-label" fontWeight="bold" margin={{ bottom: 'xxxs' }}>
								{formatFieldName(key)}
							</Box>
							<Box>{formatValue(key, value)}</Box>
						</Box>
					))}
				</ColumnLayout>
			)}
		</ExpandableSection>
	);
};

export default EntityAttributesTable;
